import { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Image as ImageIcon } from "lucide-react";

const BRAND_PALETTE = [
  { name: "Warm Ivory", hex: "#F6F2EB" },
  { name: "Aged Linen", hex: "#E8DED2" },
  { name: "Dusty Taupe", hex: "#B8A89A" },
  { name: "Muted Olive", hex: "#8B8A72" },
  { name: "Antique Brass", hex: "#B08D57" },
  { name: "Espresso Brown", hex: "#4A3F36" },
];

/**
 * Selector + state for image studio enhancement modes.
 * Surfaces: brand palette colors, transparent cutout, white studio, custom uploaded backgrounds.
 * Exposes a `getPayload()`-style derivation via the `value` it produces.
 */
export default function ImageStudioPicker({ value, onChange }) {
  // value: { mode, background_color, color_name, custom_background_path }
  const fileInput = useRef(null);
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backgrounds");
      setBackgrounds(data || []);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("backgrounds load failed", e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (patch) => onChange({ ...value, ...patch });

  const pickColor = (c) => update({ mode: "color", background_color: c.hex, color_name: c.name, custom_background_path: null });
  const pickTransparent = () => update({ mode: "transparent", custom_background_path: null });
  const pickWhite = () => update({ mode: "white", background_color: "#FFFFFF", color_name: "Pure White", custom_background_path: null });
  const pickCustom = (bg) => update({ mode: "custom", custom_background_path: bg.storage_path, color_name: bg.name });
  const pickScene = (sceneKey) => update({ mode: "scene", scene: sceneKey, custom_background_path: null });
  const pickGhost = () => update({ mode: "ghost_mannequin", custom_background_path: null });
  const pickShadow = () => update({ mode: "shadow", custom_background_path: null });

  const SCENES = [
    { key: "paris_cafe", label: "paris café" },
    { key: "linen_bedroom", label: "linen bedroom" },
    { key: "marble_flat_lay", label: "marble flat lay" },
    { key: "warehouse_studio", label: "warehouse studio" },
    { key: "brutalist_wall", label: "brutalist wall" },
    { key: "vintage_armchair", label: "vintage armchair" },
    { key: "gallery_corner", label: "gallery corner" },
    { key: "natural_window", label: "natural window" },
  ];

  const uploadBg = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", newName || file.name);
      const { data } = await api.post("/backgrounds", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("background added.");
      setNewName("");
      setBackgrounds((prev) => [data, ...prev]);
      pickCustom(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not upload background.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeBg = async (bg) => {
    if (!window.confirm(`remove "${bg.name}" from the library?`)) return;
    try {
      await api.delete(`/backgrounds/${bg.id}`);
      setBackgrounds((prev) => prev.filter((b) => b.id !== bg.id));
      if (value.custom_background_path === bg.storage_path) {
        pickColor(BRAND_PALETTE[0]);
      }
      toast.success("background removed.");
    } catch (e) {
      toast.error("could not remove background.");
    }
  };

  const mode = value?.mode || "color";

  return (
    <div className="mb-6" data-testid="image-studio-picker">
      <p className="editorial-label mb-3">image studio · choose a backdrop</p>

      {/* Mode tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="studio-mode-tabs">
        <button onClick={() => pickColor(BRAND_PALETTE[0])} data-testid="studio-mode-color" className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border ${mode === "color" ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}>brand palette</button>
        <button onClick={pickWhite} data-testid="studio-mode-white" className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border ${mode === "white" ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}>pure white</button>
        <button onClick={pickShadow} data-testid="studio-mode-shadow" className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border ${mode === "shadow" ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}>studio · shadow</button>
        <button onClick={pickGhost} data-testid="studio-mode-ghost" className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border ${mode === "ghost_mannequin" ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}>ghost mannequin</button>
        <button onClick={() => pickScene(value?.scene || "natural_window")} data-testid="studio-mode-scene" className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border ${mode === "scene" ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}>editorial scene</button>
        <button onClick={pickTransparent} data-testid="studio-mode-transparent" className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border ${mode === "transparent" ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}>transparent cutout</button>
        <button onClick={() => update({ mode: "custom" })} data-testid="studio-mode-custom" className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border ${mode === "custom" ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}>custom backdrop</button>
      </div>

      {/* Mode panels */}
      {mode === "color" && (
        <div className="flex flex-wrap gap-3" data-testid="studio-color-palette">
          {BRAND_PALETTE.map((c) => (
            <button key={c.hex} onClick={() => pickColor(c)} className={`flex items-center gap-2 border px-3 py-2 ${value?.background_color === c.hex ? "border-brass" : "border-taupe/50"}`} data-testid={`palette-${c.hex.replace('#','')}`}>
              <span className="w-4 h-4 inline-block" style={{ background: c.hex, border: '1px solid #B8A89A' }}></span>
              <span className="text-xs tracking-wider lowercase text-charcoal">{c.name}</span>
            </button>
          ))}
        </div>
      )}

      {mode === "white" && (
        <p className="text-xs text-espresso italic">studio white — clean catalogue look, ideal for e-commerce.</p>
      )}

      {mode === "shadow" && (
        <p className="text-xs text-espresso italic">studio + contact shadow — the polished commercial look. warm ivory surface with a physically-real soft shadow under the piece. eliminates the &ldquo;cut-out&rdquo; feel.</p>
      )}

      {mode === "ghost_mannequin" && (
        <p className="text-xs text-espresso italic">ghost mannequin — renders the garment with realistic 3-d volume as if worn by an invisible person. removes hangers/hooks. clean white background, catalogue-ready.</p>
      )}

      {mode === "scene" && (
        <div data-testid="studio-scene-grid">
          <p className="text-xs text-espresso italic mb-3">editorial scene — plants the piece into a curated environment. same garment, magazine-quality mood.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="studio-scene-picker">
            {SCENES.map((s) => (
              <button
                key={s.key}
                onClick={() => pickScene(s.key)}
                data-testid={`scene-${s.key}`}
                className={`text-left px-3 py-2 border transition-colors ${
                  value?.scene === s.key
                    ? "border-brass bg-brass/10 text-charcoal"
                    : "border-taupe/40 text-espresso hover:border-taupe"
                }`}
              >
                <p className="text-xs lowercase font-medium">{s.label}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === "transparent" && (
        <p className="text-xs text-espresso italic">transparent cutout — outputs the garment with no background, perfect for layouts and marketing collateral.</p>
      )}

      {mode === "custom" && (
        <div data-testid="studio-custom-bgs">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-4">
            {backgrounds.map((bg) => (
              <div
                key={bg.id}
                className={`relative aspect-square border cursor-pointer group ${value?.custom_background_path === bg.storage_path ? "border-brass ring-2 ring-brass/30" : "border-taupe/50"}`}
                onClick={() => pickCustom(bg)}
                data-testid={`custom-bg-${bg.id.slice(0, 8)}`}
              >
                <img src={fileUrl(bg.storage_path)} alt={bg.name} className="w-full h-full object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-charcoal/85 text-ivory text-[0.55rem] tracking-wider px-1 py-1 truncate">{bg.name}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeBg(bg); }}
                  className="absolute top-1 right-1 bg-ivory/90 p-1 border border-taupe opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="remove"
                >
                  <Trash2 className="w-3 h-3 text-red-700" />
                </button>
              </div>
            ))}
            <label className="aspect-square border-2 border-dashed border-taupe hover:border-brass flex flex-col items-center justify-center cursor-pointer text-taupe hover:text-brass transition-colors" data-testid="custom-bg-upload">
              {uploading ? (
                <span className="text-xs">uploading…</span>
              ) : (
                <>
                  <Plus className="w-5 h-5 mb-1" />
                  <span className="text-[0.6rem] tracking-wider uppercase">add</span>
                </>
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadBg(e.target.files?.[0])}
                disabled={uploading}
              />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="name this backdrop (optional, e.g. ‘aged linen studio’)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="custom-bg-name"
              className="flex-1 bg-transparent border-b border-taupe py-2 text-xs text-charcoal focus:outline-none focus:border-brass"
            />
            <button
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
              data-testid="custom-bg-pick"
            >
              <Upload className="w-3 h-3" /> upload backdrop
            </button>
          </div>
          {backgrounds.length === 0 && !loading && (
            <p className="text-xs text-espresso italic mt-3 flex items-center gap-2">
              <ImageIcon className="w-3 h-3" /> upload your first backdrop above — interior shots, paper textures, marble surfaces all work beautifully.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

ImageStudioPicker.defaultValue = { mode: "color", background_color: BRAND_PALETTE[0].hex, color_name: BRAND_PALETTE[0].name, custom_background_path: null };
